// ---------------------------------------------------------------------------
// PropertyInspector — right panel showing selected block/element properties.
//
// Slim orchestrator that delegates to 10 section components under ./sections/
// and reusable field components under ./fields/.
// ---------------------------------------------------------------------------

import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { AskCogoSurfaceButton } from '@/shell/ide-v2/components/ask-cogo-surface-button'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { getPageWorkPanelCopy } from '@/features/page-editor/page-work-panel-copy'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  findElementInTree,
  findParentInTree,
  collectElementIds,
} from '@/features/page-editor/store/canvas-slice'
import { cn } from '@/lib/utils'
import {
  MousePointerClick,
  ChevronRight,
  Copy,
  Trash2,
  FileText,
  Sun,
  Moon,
} from 'lucide-react'
import { MOD } from '@/lib/shortcuts'
import type { ColorScheme } from '@/features/page-editor/store/page-ui-slice'
import type { CogoElement } from '@/features/page-editor/store/types'

import { CopyableId } from './fields'
import {
  ContentSection,
  LayoutSection,
  SizeSection,
  SpacingSection,
  FillSection,
  TypographySection,
  BorderSection,
  EffectsSection,
  EventsSection,
  ClassSection,
} from './sections'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Types that have typography-related properties */
const TYPOGRAPHY_TYPES = new Set([
  'text',
  'heading',
  'button',
  'badge',
  'input',
  'textarea',
  'select',
  'checkbox',
])

/** Check if the element is a container (has children, layout, or is container type) */
function isContainerElement(element: CogoElement): boolean {
  if (
    element.type === 'container' ||
    element.type === 'section' ||
    element.type === 'card' ||
    element.type === 'form'
  )
    return true
  if (element.layout) return true
  if (element.children && element.children.length > 0) return true
  return false
}

// ---------------------------------------------------------------------------
// PropertyInspector — main component
// ---------------------------------------------------------------------------

export function PropertyInspector() {
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const selectionTier = useEditorStore((s) => s.selectionTier)
  const blocks = useEditorStore((s) => s.blocks)

  const selectedBlock = useMemo(() => {
    if (!selectedBlockName) return undefined
    return blocks[selectedBlockName]
  }, [selectedBlockName, blocks])

  const selectedElement = useMemo(() => {
    if (!selectedBlock || selectedIds.length !== 1) return undefined
    return findElementInTree(selectedBlock.ui.root, selectedIds[0])
  }, [selectedBlock, selectedIds])

  // Build element path for breadcrumb (must be before any early returns)
  const elementPath = useMemo(() => {
    if (!selectedElement || !selectedBlock) return null
    const path: { id: string; type: string; label: string }[] = []
    let result = findParentInTree(selectedBlock.ui.root, selectedElement.id)
    while (result) {
      // Use meaningful ID if it's not a generated UUID, otherwise fall back to type
      const id = result.parent.id
      const isGenerated = /^el-[0-9a-f]{8}/.test(id)
      path.unshift({
        id,
        type: result.parent.type,
        label: isGenerated ? result.parent.type : id,
      })
      result = findParentInTree(selectedBlock.ui.root, result.parent.id)
    }
    return path
  }, [selectedElement, selectedBlock])

  // -- Page settings (no element selected) --
  if (!selectedBlockName) {
    return <PageSettings />
  }

  const isBlockSelection = selectionTier === 'block'

  // Multi-element selection (not block-level)
  if (selectedIds.length > 1 && !isBlockSelection) {
    return (
      <div className="p-3 space-y-3" data-testid="inspector-multi">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
            {selectedIds.length} elements
          </span>
          <span className="text-[10px] text-muted-foreground">selected</span>
        </div>
        <Separator />
        {/* Selected element list */}
        <div className="space-y-0.5 max-h-40 overflow-y-auto">
          {selectedIds.map((id) => {
            const el = selectedBlock
              ? findElementInTree(selectedBlock.ui.root, id)
              : undefined
            const isGenerated = /^el-[0-9a-f]{8}/.test(id)
            return (
              <button
                key={id}
                type="button"
                className="flex items-center gap-2 w-full text-left px-1.5 py-1 rounded hover:bg-muted/50 text-xs transition-colors"
                onClick={() =>
                  useEditorStore.getState().select(id, selectedBlockName!)
                }
              >
                <span className="text-muted-foreground shrink-0">
                  {el?.type ?? '?'}
                </span>
                <span className="truncate font-mono text-[10px] text-foreground/60">
                  {isGenerated ? id.slice(0, 11) : id}
                </span>
              </button>
            )
          })}
        </div>
        <Separator />
        {/* Quick actions */}
        <div className="flex gap-1">
          <button
            type="button"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xl border border-input hover:bg-muted transition-colors"
            onClick={() => useEditorStore.getState().copySelection()}
            title="Copy selection"
          >
            <Copy className="h-3 w-3" />
            Copy
          </button>
          <button
            type="button"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xl border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
            onClick={() => {
              const { selectedIds: ids, selectedBlockName: bn } =
                useEditorStore.getState()
              if (bn) {
                for (const id of ids) {
                  useEditorStore.getState().removeElement(bn, id)
                }
                useEditorStore.getState().clearSelection()
              }
            }}
            title="Delete selection"
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </button>
        </div>
      </div>
    )
  }

  return (
    <ScrollArea className="h-full" data-testid="property-inspector">
      <div className="p-3">
        {/* Breadcrumb navigation — Framer-style */}
        {selectedElement && elementPath && (
          <div className="flex items-center gap-0.5 mb-2 flex-wrap">
            <BreadcrumbItem
              label={selectedBlockName}
              onClick={() =>
                useEditorStore.getState().selectBlock(selectedBlockName!)
              }
            />
            {elementPath.map((node, index) => (
              <BreadcrumbItem
                key={`${node.id || node.type}:${index}`}
                label={node.label}
                onClick={() =>
                  useEditorStore.getState().select(node.id, selectedBlockName!)
                }
                showChevron
              />
            ))}
            <ChevronRight className="h-2.5 w-2.5 text-muted-foreground/40 shrink-0" />
            <span className="text-[11px] font-medium text-foreground">
              {/^el-[0-9a-f]{8}/.test(selectedElement.id)
                ? selectedElement.type
                : selectedElement.id}
            </span>
          </div>
        )}

        {/* Header — compact single-line */}
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold">
            {selectedElement?.type ??
              selectedBlock?.meta?.name ??
              selectedBlockName}
          </h3>
          {selectedElement && (
            <span
              className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded"
              style={{
                backgroundColor: 'hsl(var(--editor-selection) / 0.1)',
                color: 'hsl(var(--editor-selection))',
              }}
            >
              {selectedElement.type}
            </span>
          )}
          {!selectedElement &&
            selectedBlock?.meta?.type &&
            selectedBlock.meta?.type !==
              (selectedBlock?.meta?.name ?? selectedBlockName) && (
              <span className="text-xs text-muted-foreground">
                {selectedBlock.meta?.type}
              </span>
            )}
          <span className="flex-1" />
          {selectedElement?.children && selectedElement.children.length > 0 && (
            <span className="text-[10px] text-muted-foreground/60">
              {selectedElement.children.length} children
            </span>
          )}
          {/* Show ID only when it differs from the display name (avoids redundancy) */}
          {!selectedElement &&
            selectedBlockName !==
              (selectedBlock?.meta?.name ?? selectedBlockName) && (
              <span className="text-[10px] text-muted-foreground font-mono">
                {selectedBlockName}
              </span>
            )}
        </div>

        <Separator className="mb-2" />

        {/* Block-level selection OR block selected with no specific element */}
        {(isBlockSelection || (!selectedElement && selectedBlock)) &&
          selectedBlock && (
            <BlockStructureInfo
              rootType={selectedBlock.ui.root.type}
              elementCount={collectElementIds(selectedBlock.ui.root).length}
              rootClassName={selectedBlock.ui.root.className}
            />
          )}

        {/* Single element selection — collapsible sections */}
        {!isBlockSelection && selectedElement && selectedBlockName && (
          <ElementEditor
            elementId={selectedElement.id}
            blockName={selectedBlockName}
            element={selectedElement}
          />
        )}
      </div>
    </ScrollArea>
  )
}

// ---------------------------------------------------------------------------
// BlockStructureInfo — read-only block overview
// ---------------------------------------------------------------------------

function BlockStructureInfo({
  rootType,
  elementCount,
  rootClassName,
}: {
  rootType: string
  elementCount: number
  rootClassName?: string
}) {
  return (
    <div className="space-y-2 text-sm">
      <h4 className="text-xs font-medium text-muted-foreground mb-2">
        Block Structure
      </h4>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Root Type</span>
        <span>{rootType}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Elements</span>
        <span>{elementCount}</span>
      </div>
      {rootClassName && (
        <div>
          <span className="text-muted-foreground text-xs">Root Classes</span>
          <p className="mt-1 font-mono text-xs break-all bg-muted/50 rounded p-1.5">
            {rootClassName}
          </p>
        </div>
      )}
      <Separator className="my-2" />
      <p className="text-xs text-muted-foreground">
        Click an element in the canvas to inspect it
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ElementEditor — slim orchestrator rendering 10 section components
// ---------------------------------------------------------------------------

function ElementEditor({
  elementId,
  blockName,
  element,
}: {
  elementId: string
  blockName: string
  element: CogoElement
}) {
  const updateElement = useEditorStore((s) => s.updateElement)

  const updateProp = useCallback(
    (updates: Partial<CogoElement>) => {
      updateElement(blockName, elementId, updates)
    },
    [updateElement, blockName, elementId],
  )

  /** Update className by applying a transform function */
  const updateClass = useCallback(
    (transform: (current: string) => string) => {
      const current = element.className ?? ''
      const updated = transform(current)
      if (updated !== current) {
        updateProp({ className: updated })
      }
    },
    [element.className, updateProp],
  )

  const showLayout = isContainerElement(element)
  const showTypography = TYPOGRAPHY_TYPES.has(element.type)

  return (
    <div data-testid="element-editor">
      {/* Element identity — clickable ID row (copies to clipboard with feedback) */}
      <CopyableId id={elementId} />

      {/* ---- Section 1: Content ---- */}
      <ContentSection element={element} updateProp={updateProp} />

      {/* ---- Section 2: Layout (containers only) ---- */}
      {showLayout && (
        <LayoutSection
          element={element}
          updateProp={updateProp}
          updateClass={updateClass}
        />
      )}

      {/* ---- Section 3: Size ---- */}
      <SizeSection element={element} updateClass={updateClass} />

      {/* ---- Section 4: Spacing ---- */}
      <SpacingSection
        element={element}
        updateProp={updateProp}
        updateClass={updateClass}
      />

      {/* ---- Section 5: Fill ---- */}
      <FillSection element={element} updateClass={updateClass} />

      {/* ---- Section 6: Typography (text-like types) ---- */}
      {showTypography && (
        <TypographySection element={element} updateClass={updateClass} />
      )}

      {/* ---- Section 7: Border ---- */}
      <BorderSection element={element} updateClass={updateClass} />

      {/* ---- Section 8: Effects ---- */}
      <EffectsSection element={element} updateClass={updateClass} />

      {/* ---- Section 9: Events ---- */}
      <EventsSection
        elementId={elementId}
        blockName={blockName}
        element={element}
      />

      {/* ---- Section 10: Class ---- */}
      <ClassSection element={element} updateProp={updateProp} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// BreadcrumbItem — clickable path segment for element navigation
// ---------------------------------------------------------------------------

function BreadcrumbItem({
  label,
  onClick,
  showChevron = false,
}: {
  label: string
  onClick: () => void
  showChevron?: boolean
}) {
  return (
    <span className="contents">
      {showChevron && (
        <ChevronRight className="h-2.5 w-2.5 text-muted-foreground/40 shrink-0" />
      )}
      <button
        onClick={onClick}
        className="text-[11px] text-muted-foreground hover:text-foreground transition-colors truncate max-w-[80px]"
        title={label}
      >
        {label}
      </button>
    </span>
  )
}

// ---------------------------------------------------------------------------
// PageSettings — shown when no element is selected (page-level inspector)
// ---------------------------------------------------------------------------

function PageSettings() {
  const activePage = useEditorStore((s) => {
    const app = s.app
    const pageId = s.activePageId
    if (!app || !pageId) return undefined
    return app.pages.find((p) => p.id === pageId)
  })
  const app = useEditorStore((s) => s.app)
  const activePageId = useEditorStore((s) => s.activePageId)
  const previewColorScheme = useEditorStore((s) => s.previewColorScheme)
  const setPreviewColorScheme = useEditorStore((s) => s.setPreviewColorScheme)
  const updatePageMeta = useEditorStore((s) => s.updatePageMeta)
  const renamePage = useEditorStore((s) => s.renamePage)
  const setPageLayout = useEditorStore((s) => s.setPageLayout)
  const setDefaultPage = useEditorStore((s) => s.setDefaultPage)
  const duplicatePage = useEditorStore((s) => s.duplicatePage)
  const removePage = useEditorStore((s) => s.removePage)
  const { locale } = useStudioLocale()
  const copy = getPageWorkPanelCopy(locale)

  const [editId, setEditId] = useState(activePage?.id ?? '')
  const [editTitle, setEditTitle] = useState(activePage?.title ?? '')
  const [editDesc, setEditDesc] = useState(
    (activePage as any)?.description ?? '',
  )
  const [editRoute, setEditRoute] = useState(activePage?.route ?? '/')
  const [pageIdError, setPageIdError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  // Sync local state when page changes
  const pageId = activePage?.id
  const pageTitle = activePage?.title
  const pageDesc = (activePage as any)?.description
  const pageRoute = activePage?.route
  useEffect(() => {
    setEditId(pageId ?? '')
    setEditTitle(pageTitle ?? '')
    setEditDesc(pageDesc ?? '')
    setEditRoute(pageRoute ?? '/')
    setPageIdError(null)
    setDeleteConfirm(false)
  }, [pageId, pageTitle, pageDesc, pageRoute])

  const isDefaultRoute = app?.navigation?.defaultRoute === activePage?.route
  const pageDetailChatContext = (operation: string) =>
    [
      `Studio page details operation=${operation}`,
      `Page=${activePage?.id ?? 'none'}`,
      `Title=${activePage?.title ?? activePage?.id ?? 'none'}`,
      `Route=${activePage?.route ?? '/'}`,
      `Blocks=${activePage?.blocks?.length ?? 0}`,
      `Default route=${isDefaultRoute}`,
    ].join('; ')

  if (!activePage) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-3 p-6"
        data-testid="inspector-empty"
      >
        <div className="rounded-xl bg-muted/50 p-3">
          <MousePointerClick className="h-6 w-6 text-muted-foreground/40" />
        </div>
        <p className="text-sm text-muted-foreground text-center">
          {copy.loadTemplateToEdit}
        </p>
      </div>
    )
  }

  const commitTitle = () => {
    if (editTitle !== (activePage.title ?? '') && activePageId) {
      updatePageMeta(activePageId, { title: editTitle })
    }
  }

  const commitPageId = () => {
    if (!activePageId) return
    const trimmed = editId.trim()
    if (!trimmed) {
      setPageIdError(copy.pageIdRequired)
      setEditId(activePageId)
      return
    }
    if (trimmed === activePageId) {
      setPageIdError(null)
      return
    }
    if (app?.pages.some((page) => page.id === trimmed)) {
      setPageIdError(copy.pageIdExists)
      return
    }
    renamePage(activePageId, trimmed)
    useEditorStore.getState().selectPage(trimmed)
    setPageIdError(null)
  }

  const commitDesc = () => {
    if (editDesc !== ((activePage as any).description ?? '') && activePageId) {
      updatePageMeta(activePageId, { description: editDesc })
    }
  }

  const commitRoute = () => {
    if (!activePageId) return
    const trimmed = editRoute.trim()
    if (!trimmed) {
      setEditRoute(activePage.route ?? '/')
      return
    }
    const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
    if (normalized !== (activePage.route ?? '/')) {
      updatePageMeta(activePageId, { route: normalized })
    }
    setEditRoute(normalized)
  }

  const handleDuplicatePage = () => {
    if (!activePageId) return
    duplicatePage(activePageId)
    const nextPageId = useEditorStore.getState().activePageId
    if (nextPageId) useEditorStore.getState().selectPage(nextPageId)
  }

  const handleDeletePage = () => {
    if (!activePageId || (app?.pages.length ?? 0) <= 1) return
    removePage(activePageId)
    const nextPageId = useEditorStore.getState().activePageId
    if (nextPageId) useEditorStore.getState().selectPage(nextPageId)
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-3 space-y-4" data-testid="page-settings">
        {/* Page header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{activePage.id}</p>
              <p className="truncate text-[10px] text-muted-foreground">
                {activePage.route ?? '/'}
              </p>
            </div>
          </div>
          <AskCogoSurfaceButton
            label={copy.reviewPage}
            className="h-7 px-2 text-[11px]"
            operationId="page.details.review"
            operationKind="page-detail"
            chatContextPatch={{
              surfaceId: 'page-inspector',
              pageId: activePage.id,
              pageName: activePage.title ?? activePage.id,
            }}
            context={() =>
              [
                'Studio page details: review and edit this page title, description, route, blocks, default route, and page-level layout through chat.',
                `Page=${activePage.id}`,
                `Title=${activePage.title ?? activePage.id}`,
                `Route=${activePage.route ?? '/'}`,
                `Blocks=${activePage.blocks?.length ?? 0}`,
                `Default route=${isDefaultRoute}`,
                'Chat operations=rename page, change route, review blocks, adjust page layout',
              ].join('; ')
            }
          />
        </div>
        <div
          className="grid grid-cols-2 gap-2"
          data-testid="page-detail-chat-ops"
        >
          <AskCogoSurfaceButton
            label={copy.rename}
            className="h-7 justify-center px-2 text-[11px]"
            operationId="page.details.rename"
            operationKind="page-detail"
            chatContextPatch={{
              surfaceId: 'page-inspector',
              pageId: activePage.id,
              pageName: activePage.title ?? activePage.id,
              selectedPropertyKey: 'title',
            }}
            context={() => pageDetailChatContext('rename')}
          />
          <AskCogoSurfaceButton
            label={copy.route}
            className="h-7 justify-center px-2 text-[11px]"
            operationId="page.details.route"
            operationKind="page-detail"
            chatContextPatch={{
              surfaceId: 'page-inspector',
              pageId: activePage.id,
              pageName: activePage.title ?? activePage.id,
              selectedPropertyKey: 'route',
            }}
            context={() => pageDetailChatContext('route')}
          />
          <AskCogoSurfaceButton
            label={copy.blocks}
            className="h-7 justify-center px-2 text-[11px]"
            operationId="page.details.blocks"
            operationKind="page-detail"
            chatContextPatch={{
              surfaceId: 'page-inspector',
              pageId: activePage.id,
              pageName: activePage.title ?? activePage.id,
              selectedPropertyKey: 'blocks',
            }}
            context={() => pageDetailChatContext('blocks')}
          />
          <AskCogoSurfaceButton
            label={copy.layout}
            className="h-7 justify-center px-2 text-[11px]"
            operationId="page.details.layout"
            operationKind="page-detail"
            chatContextPatch={{
              surfaceId: 'page-inspector',
              pageId: activePage.id,
              pageName: activePage.title ?? activePage.id,
              selectedPropertyKey: 'layout',
            }}
            context={() => pageDetailChatContext('layout')}
          />
        </div>

        <Separator />

        {/* Identity */}
        <div className="space-y-2">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            {copy.identity}
          </p>
          <div className="space-y-2">
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground">
                {copy.pageId}
              </label>
              <input
                type="text"
                aria-label={copy.pageId}
                data-testid="page-id-input"
                value={editId}
                onChange={(e) => {
                  setEditId(e.target.value)
                  setPageIdError(null)
                }}
                onBlur={commitPageId}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitPageId()
                  if (e.key === 'Escape') {
                    setEditId(activePage.id)
                    setPageIdError(null)
                  }
                }}
                aria-invalid={pageIdError != null}
                aria-describedby={pageIdError ? 'page-id-error' : undefined}
                className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
              {pageIdError && (
                <p id="page-id-error" className="text-[10px] text-destructive">
                  {pageIdError}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label
                htmlFor="page-title-input"
                className="text-[11px] text-muted-foreground"
              >
                {copy.title}
              </label>
              <input
                id="page-title-input"
                type="text"
                aria-label={copy.pageTitleAria}
                data-testid="page-title-input"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTitle()
                }}
                placeholder={activePage.id}
                className="w-full px-2 py-1 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor="page-description-input"
                className="text-[11px] text-muted-foreground"
              >
                {copy.description}
              </label>
              <input
                id="page-description-input"
                type="text"
                aria-label={copy.pageDescriptionAria}
                data-testid="page-description-input"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                onBlur={commitDesc}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitDesc()
                }}
                placeholder={copy.descriptionPlaceholder}
                className="w-full px-2 py-1 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground">{copy.route}</label>
              <input
                type="text"
                aria-label={copy.route}
                value={editRoute}
                onChange={(e) => setEditRoute(e.target.value)}
                onBlur={commitRoute}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRoute()
                }}
                placeholder="/page-route"
                className="w-full px-2 py-1 text-xs rounded border border-border bg-background font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {copy.layout}
          </p>
          <div
            className="grid grid-cols-2 gap-1"
            role="group"
            aria-label={copy.layoutAria}
          >
            {(
              [
                ['flow', copy.layoutFlow],
                ['absolute', copy.layoutFreeform],
              ] as const
            ).map(([layout, label]) => {
              const active =
                layout === 'absolute'
                  ? activePage.layout === 'absolute'
                  : activePage.layout !== 'absolute'
              return (
                <button
                  key={layout}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setPageLayout(activePage.id, layout)}
                  className={cn(
                    'h-8 rounded-md border px-2 text-xs font-medium transition-colors',
                    active
                      ? 'border-primary/40 bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        <Separator />

        {/* Preview */}
        <div className="space-y-2">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            {copy.preview}
          </p>
          <div className="space-y-1">
            <label className="text-[11px] text-muted-foreground">
              {copy.colorScheme}
            </label>
            <div className="flex gap-1">
              <ColorSchemeButton
                scheme="light"
                active={previewColorScheme === 'light'}
                onClick={() => setPreviewColorScheme('light')}
                label={copy.light}
              />
              <ColorSchemeButton
                scheme="dark"
                active={previewColorScheme === 'dark'}
                onClick={() => setPreviewColorScheme('dark')}
                label={copy.dark}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Page info */}
        <div className="space-y-2">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            {copy.pageInfo}
          </p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{copy.route}</span>
              <span className="text-[11px] font-mono">
                {activePage.route ?? '/'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{copy.blocks}</span>
              <span className="text-[11px]">
                {activePage.blocks?.length ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                {copy.defaultRoute}
              </span>
              <span className="text-[11px]">
                {isDefaultRoute ? '\u2713' : '\u2013'}
              </span>
            </div>
            {!isDefaultRoute && (
              <button
                type="button"
                onClick={() => setDefaultPage(activePage.id)}
                className="mt-1 h-7 w-full rounded-md border border-border px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
              >
                {copy.setAsDefaultPage}
              </button>
            )}
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {copy.pageActions}
          </p>
          <button
            type="button"
            onClick={handleDuplicatePage}
            className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          >
            <Copy className="h-3.5 w-3.5" />
            {copy.duplicatePage}
          </button>
          {deleteConfirm ? (
            <div className="grid grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                className="h-8 rounded-md border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted"
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                onClick={handleDeletePage}
                className="h-8 rounded-md border border-destructive/40 bg-destructive px-2 text-xs font-medium text-destructive-foreground transition-colors hover:bg-destructive/90"
              >
                {copy.delete}
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={(app?.pages.length ?? 0) <= 1}
              onClick={() => setDeleteConfirm(true)}
              className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-destructive/30 px-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {copy.deletePage}
            </button>
          )}
        </div>

        <Separator />

        {/* Hint */}
        <div className="space-y-1 text-center pt-2">
          <p className="text-[10px] text-muted-foreground/50">
            {copy.clickToEdit}
          </p>
          <p className="text-[10px] text-muted-foreground/50">
            {copy.holdToMultiSelect(MOD.replace('+', ''))}
          </p>
        </div>
      </div>
    </ScrollArea>
  )
}

function ColorSchemeButton({
  scheme,
  active,
  onClick,
  label,
}: {
  scheme: ColorScheme
  active: boolean
  onClick: () => void
  label: string
}) {
  const Icon = scheme === 'light' ? Sun : Moon

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs transition-colors',
        active
          ? 'border-primary bg-primary/10 text-foreground font-medium'
          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )
}
